import React from "react"
import * as Yup from "yup"
import {Formik} from "formik"
import TextField from "../../../Address/Textfield"
import Card from "../../../../containers/Card/Card"
import AddressComponent from "../../../Address/Address"
import {US_COUNTRY_CODE} from "../../../../resources/address"
import {
	BusinessAnnualRevenue,
	BusinessNumberOfEmployees,
	BusinessVertical,
	CashFlow,
} from "../../../../resources/application"
import {Address, Phone} from "../../../../resources/common"
import LeftRightSlots from "../../../../containers/LeftRightSlots/LeftRightSlots"
import PhoneNumber from "../../../Address/PhoneNumber"
import {ApplicationResubmitForms} from "../../../../pages/ReevaluateApplication/ReevaluatePage"
import reevaluateBusinessValidationSchema from "./reevaluateBusinessValidationSchema"
import Dropdown from "../../../Address/Dropdown"
import CheckboxField from "../../../CheckboxField/CheckboxField"

export type ReevaluatedBusinessApplicationDataRequest = Omit<
	ReevaluatedBusinessApplicationData & {website?: string | null},
	"hasNoWebsite"
>

export interface ReevaluatedBusinessApplicationData {
	name: string
	dba?: string
	address: Address
	ein: string
	phone: Phone
	businessVertical: BusinessVertical
	yearOfIncorporation: string
	hasNoWebsite: boolean
	website?: string
	stockSymbol?: string
	numberOfEmployees?: BusinessNumberOfEmployees | ""
	annualRevenue?: BusinessAnnualRevenue | ""
	cashFlow?: CashFlow | ""
}

export default function ReevaluateBusinessForm(props: {
	businessApplication: ReevaluatedBusinessApplicationData
	submitCallback: (values: ReevaluatedBusinessApplicationData) => void
	formId: ApplicationResubmitForms
}) {
	const {businessApplication, submitCallback, formId} = props

	const validationSchema = Yup.object().shape(reevaluateBusinessValidationSchema)

	const initialFormValues = {
		address: {
			street: businessApplication.address?.street,
			street2: businessApplication.address?.street2,
			city: businessApplication.address?.city,
			state: businessApplication.address?.state ?? "",
			postalCode: businessApplication.address?.postalCode,
			country: businessApplication.address?.country ?? US_COUNTRY_CODE,
		},
		ein: businessApplication.ein,
		phone: businessApplication.phone,
		name: businessApplication.name,
		website: businessApplication.website,
		dba: businessApplication.dba,
		businessVertical: businessApplication.businessVertical,
		yearOfIncorporation: businessApplication.yearOfIncorporation,
		numberOfEmployees: businessApplication.numberOfEmployees,
		annualRevenue: businessApplication.annualRevenue,
		stockSymbol: businessApplication.stockSymbol,
		cashFlow: businessApplication.cashFlow,
		hasNoWebsite: businessApplication.hasNoWebsite,
	}

	const title = `Business - ${businessApplication.name}`

	return (
		<Card className="reevaluate-form" title={title}>
			<Formik
				initialValues={initialFormValues}
				validationSchema={validationSchema}
				onSubmit={(values) => {
					submitCallback({
						...values,
						numberOfEmployees: values.numberOfEmployees === "" ? undefined : values.numberOfEmployees,
						annualRevenue: values.annualRevenue === "" ? undefined : values.annualRevenue,
						cashFlow: values.cashFlow === "" ? undefined : values.cashFlow,
					})
				}}
			>
				{({values, handleSubmit, handleChange, setFieldValue, handleBlur}) => {
					return (
						<form noValidate onSubmit={handleSubmit} id={formId}>
							<LeftRightSlots
								left={
									<>
										<TextField
											label="Business Name"
											placeholder="Enter Business Name"
											name="name"
											value={values.name}
											onChange={handleChange}
											onBlur={handleBlur}
										/>

										<TextField
											label="EIN"
											placeholder="Enter EIN"
											name="ein"
											value={values.ein}
											onChange={handleChange}
											onBlur={handleBlur}
										/>

										<TextField
											label="Doing Buisness As"
											placeholder="Enter dba"
											name="dba"
											value={values.dba ?? ""}
											onChange={(value) => {
												handleChange(value)
											}}
											onBlur={handleBlur}
										/>
										<Dropdown
											name="businessVertical"
											label="Business Vertical"
											value={values.businessVertical}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{Object.entries(BusinessVertical).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<TextField
											label="Year Of Incorporation"
											placeholder="Enter Year Of Incorporation"
											name="yearOfIncorporation"
											value={values.yearOfIncorporation}
											onChange={handleChange}
											onBlur={handleBlur}
										/>

										<Dropdown
											name="numberOfEmployees"
											label="Number Of Employees"
											value={values.numberOfEmployees}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{businessApplication.numberOfEmployees ? <React.Fragment /> : <option value="" />}
											{Object.entries(BusinessNumberOfEmployees).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<Dropdown
											name="annualRevenue"
											label="Annual Revenue"
											value={values.annualRevenue}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{businessApplication.annualRevenue ? <React.Fragment /> : <option value="" />}
											{Object.entries(BusinessAnnualRevenue).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<TextField
											label="Stock Symbol"
											placeholder="Enter Stock Symbol"
											name="stockSymbol"
											value={values.stockSymbol ?? ""}
											onChange={(value) => {
												handleChange(value)
											}}
											onBlur={handleBlur}
										/>

										<Dropdown
											name="cashFlow"
											label="Cash Flow"
											value={values.cashFlow}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{businessApplication.cashFlow ? <React.Fragment /> : <option value="" />}
											{Object.entries(CashFlow).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<PhoneNumber
											name="phoneNumber"
											label="Phone Number"
											countryCodeName="phone.countryCode"
											phoneNumberName="phone.number"
											countryCodeValue={values.phone.countryCode}
											phoneNumberValue={values.phone?.number}
											countryCodeOnChange={(value) => {
												setFieldValue("phone.countryCode", value)
											}}
											phoneNumberOnChange={(value) => {
												setFieldValue("phone.number", value)
											}}
											countryCodeOnBlur={handleBlur}
											phoneNumberOnBlur={handleBlur}
											infoTooltipText="Enter the country code followed by the phone number."
										/>

										<div className={"website"}>
											<TextField
												label="Website"
												placeholder="Enter website"
												name="website"
												value={values.website ?? ""}
												onChange={(value) => {
													handleChange(value)
												}}
												onBlur={handleBlur}
												disabled={values.hasNoWebsite}
											/>
											<CheckboxField
												name="hasNoWebsite"
												label="This business has no website"
												checked={values.hasNoWebsite}
												onBlur={handleBlur}
												onChange={(value) => {
													const isChecked = value.target.checked
													if (isChecked) {
														setFieldValue("website", "", true)
													} else {
														setFieldValue("website", businessApplication.website, true)
													}
													handleChange(value)
												}}
											/>
										</div>
									</>
								}
								right={
									<AddressComponent
										closed={false}
										namePrefix="address."
										street={values.address.street}
										street2={values.address.street2}
										city={values.address.city}
										country={values.address.country || ""}
										state={values.address.state}
										postalCode={values.address.postalCode}
										isUsOnlyAddress={false}
										handleChange={handleChange}
										handleBlur={handleBlur}
										setFieldValue={setFieldValue}
									/>
								}
							/>
						</form>
					)
				}}
			</Formik>
		</Card>
	)
}
